import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { BookOpen, MapPin, IndianRupee, Trash2, Edit, Eye, EyeOff } from 'lucide-react';
import apiClient from '../../services/apiClient';

export const MyTuitionPosts = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetchMyPosts();
  }, []);

  const fetchMyPosts = async () => {
    try {
      const response = await apiClient.get('/tuition-posts/my');
      setPosts(response.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (postId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'closed' : 'active';
      await apiClient.put(`/tuition-posts/${postId}/status?status=${newStatus}`);
      fetchMyPosts();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await apiClient.delete(`/tuition-posts/${postId}`);
      fetchMyPosts();
    } catch (error) {
      alert('Failed to delete post');
    }
  };

  if (user?.role !== 'student') {
    return <div className="p-8 text-center">Only students can manage tuition posts</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Tuition Posts</h1>
            <p className="text-gray-600">Manage your tuition requirements</p>
          </div>
          <Button
            onClick={() => navigate('/tuition-posts/create')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Create New Post
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : posts.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-gray-600 mb-4">You haven't created any tuition posts yet</p>
              <Button onClick={() => navigate('/tuition-posts/create')}>
                Create Your First Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <Card key={post.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold">{post.category}</h3>
                        <Badge className={post.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>
                          {post.status}
                        </Badge>
                      </div>
                      <p className="text-gray-700 mb-3">{post.description}</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.subjects.map((subject, idx) => (
                          <Badge key={idx} variant="secondary">{subject}</Badge>
                        ))}
                      </div>
                      <div className="flex items-center space-x-6 text-sm text-gray-600">
                        <span className="flex items-center space-x-1">
                          <MapPin className="h-4 w-4" />
                          <span>{post.city}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <IndianRupee className="h-4 w-4" />
                          <span>₹{post.budget_amount}/{post.budget_type === 'hourly' ? 'hr' : 'month'}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <BookOpen className="h-4 w-4" />
                          <span>{post.board.toUpperCase()}</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleStatus(post.id, post.status)}
                        title={post.status === 'active' ? 'Mark as Closed' : 'Mark as Active'}
                      >
                        {post.status === 'active' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deletePost(post.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
